/**
 * Structural Summary dashboard types — issue #819, Phase 1.
 *
 * The payload returned by GET /dashboard/structural/summary. Schema is
 * versioned so adapters and future phases (Operational, Security) can
 * evolve it without breaking the frontend.
 *
 * Source-of-truth rule: every numeric here MUST be derivable from data
 * the analyzer already computes — no external calls. Phases 2 and 3
 * add the Operational and Security payloads as sibling shapes.
 */
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────
// Executive tiles — the six top-line numbers.
// ─────────────────────────────────────────────────────────────────
export const StructuralTilesSchema = z.object({
    userExperiences: z.number().int().nonnegative(),
    apiEndpoints: z.number().int().nonnegative(),
    /** Flows where every ApiCallSite resolves with confidence ≥ 0.9. */
    unambiguousE2EFlows: z.number().int().nonnegative(),
    ambiguousStitches: z.number().int().nonnegative(),
    unresolvedStitches: z.number().int().nonnegative(),
    /** 0..1; % of call-graph / fcg / bcg nodes carrying CodeEvidence. */
    evidenceCoverage: z.number().min(0).max(1),
    /**
     * Number of projects skipped for stitch-derived tiles because they
     * have no localPath (GitHub-only, never cloned). Lets the UI render
     * a "partial data" badge.
     */
    stitchesUnavailableForProjects: z.number().int().nonnegative(),
});
// ─────────────────────────────────────────────────────────────────
// Hotspots & orphans — structural extremes of the call graph.
// ─────────────────────────────────────────────────────────────────
export const HotspotRowSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    functionId: z.string(),
    functionName: z.string(),
    filePath: z.string().optional(),
    lineStart: z.number().int().optional(),
    callerCount: z.number().int().nonnegative(),
    calledCount: z.number().int().nonnegative(),
    side: z.enum(['frontend', 'backend', 'unknown']),
});
export const OrphanRowSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    functionId: z.string(),
    functionName: z.string(),
    filePath: z.string().optional(),
    lineStart: z.number().int().optional(),
});
/**
 * Issue #907: Hot endpoints — backend route handlers ranked by the
 * number of FE call sites that resolve to them via the auto-stitcher.
 * Distinct from the in-process hotspots panel: callerCount here is
 * stitch fan-in (HTTP-layer reachability), not AST in-process callers.
 */
export const HotEndpointRowSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    endpointId: z.string(),
    method: z.string(),
    path: z.string(),
    framework: z.string(),
    /** Handler function name (qualified or bare). Optional — some
     * frameworks expose anonymous inline handlers. */
    handlerName: z.string().optional(),
    filePath: z.string().optional(),
    lineStart: z.number().int().optional(),
    /** Total stitches pointing to this endpoint (confident + ambiguous). */
    callerCount: z.number().int().nonnegative(),
    /** Stitches with confidence ≥ 0.9. */
    confidentCallerCount: z.number().int().nonnegative(),
    /** Stitches with confidence < 0.9 (still real call sites; the
     * mapping is just less certain). */
    ambiguousCallerCount: z.number().int().nonnegative(),
    /** Authentication requirement on the endpoint, when known. */
    requiresAuth: z.boolean().optional(),
});
// ─────────────────────────────────────────────────────────────────
// Health strip — five stacked distributions.
// ─────────────────────────────────────────────────────────────────
/** Stacked segments of a 100% bar. */
export const DistributionSegmentSchema = z.object({
    label: z.string(),
    value: z.number().nonnegative(),
    /** Optional stable color hint so UI can keep colors consistent. */
    tone: z
        .enum(['green', 'amber', 'red', 'blue', 'purple', 'slate'])
        .optional(),
});
export const HealthStripSchema = z.object({
    stitchConfidence: z.array(DistributionSegmentSchema),
    endpointAuth: z.array(DistributionSegmentSchema),
    frameworks: z.array(DistributionSegmentSchema),
    /** Complexity histogram: buckets 1..5, 6..10, 11..20, 20+. */
    complexity: z.array(DistributionSegmentSchema),
    /** Dynamic endpoints: resolvable vs dynamic. */
    dynamicEndpoints: z.array(DistributionSegmentSchema),
});
// ─────────────────────────────────────────────────────────────────
// Graph structure metrics — coupling, fan-out, external surface.
// ─────────────────────────────────────────────────────────────────
export const CouplingRowSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    module: z.string(),
    afferent: z.number().int().nonnegative(),
    efferent: z.number().int().nonnegative(),
});
export const ExternalServiceRowSchema = z.object({
    host: z.string(),
    callCount: z.number().int().nonnegative(),
    projects: z.number().int().nonnegative(),
});
export const DbTableRowSchema = z.object({
    table: z.string(),
    projects: z.number().int().nonnegative(),
    queries: z.number().int().nonnegative(),
});
export const GraphStructureSchema = z.object({
    topAfferent: z.array(CouplingRowSchema),
    topEfferent: z.array(CouplingRowSchema),
    externalServices: z.array(ExternalServiceRowSchema),
    dbTables: z.array(DbTableRowSchema),
});
// ─────────────────────────────────────────────────────────────────
// Cross-project matrix.
// ─────────────────────────────────────────────────────────────────
export const ProjectMatrixRowSchema = z.object({
    projectId: z.string(),
    projectName: z.string(),
    /** ISO string; when the last analysis for this project completed. */
    lastAnalyzedAt: z.string().nullable(),
    /** true if project has no localPath — stitch-derived fields will be null. */
    stitchesAvailable: z.boolean(),
    userExperiences: z.number().int().nonnegative(),
    apiEndpoints: z.number().int().nonnegative(),
    unambiguousE2EFlows: z.number().int().nonnegative().nullable(),
    ambiguousStitches: z.number().int().nonnegative().nullable(),
    unresolvedStitches: z.number().int().nonnegative().nullable(),
    evidenceCoverage: z.number().min(0).max(1),
});
// ─────────────────────────────────────────────────────────────────
// Top-level payload.
// ─────────────────────────────────────────────────────────────────
export const StructuralSummaryPayloadSchema = z.object({
    schemaVersion: z.literal(1),
    /** ISO string when this response was computed. */
    generatedAt: z.string(),
    /** null for "all projects" view; set when filtered to one. */
    projectId: z.string().nullable(),
    projectCount: z.number().int().nonnegative(),
    tiles: StructuralTilesSchema,
    hotspots: z.array(HotspotRowSchema),
    orphans: z.array(OrphanRowSchema),
    /** Issue #907: top BE endpoints ranked by stitched FE call sites. */
    hotEndpoints: z.array(HotEndpointRowSchema).default([]),
    healthStrip: HealthStripSchema,
    graphStructure: GraphStructureSchema,
    projectMatrix: z.array(ProjectMatrixRowSchema),
});
//# sourceMappingURL=dashboardSummary.js.map